import { ApolloConsumer } from '@apollo/client';
import { SearchPageType } from '@zoopla/listing-search-context';
import { GEO_LOCATIONS_QUERY } from '@zoopla/listing-search-queries';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { FC, KeyboardEvent } from 'react';
import { InputAutoSuggestWithLabel } from './InputAutoSuggestWithLabel/InputAutoSuggestWithLabel';
import { transformGeoLocationData } from './transforms';

type Tab = SearchPageType | 'house-prices';

type LocationSearchInputProps = {
    id?: string;
    onLocationInputKeyDown: () => void;
    onInputTextChange?: (value: string) => void;
    hasExtendedHeight?: boolean;
    hasBorderRight?: boolean;
    hasDarkPurpleBorder?: boolean;
    onlyDisplayRemoveIconWhenActive?: boolean;
    searchType?: Tab;
    value: string;
    showError?: boolean;
    onLocationSelect?: ({
        identifier,
        q
    }: {
        identifier: string;
        q: string;
    }) => void;
    handleInputTextChange?: (value: string) => void;
};

const forSaleToRentPlaceholder = 'eg. Oxford or NW3';

export const LocationSearchInput: FC<
    React.PropsWithChildren<LocationSearchInputProps>
> = ({
    id,
    onLocationInputKeyDown,
    hasExtendedHeight,
    hasBorderRight,
    hasDarkPurpleBorder,
    onlyDisplayRemoveIconWhenActive,
    searchType,
    value,
    showError = false,
    onLocationSelect,
    handleInputTextChange
}) => {
    const handleInputKeyDown = (evt: KeyboardEvent) => {
        evt.preventDefault();

        if (evt.key === 'Enter') {
            onLocationInputKeyDown();
        }
    };

    return (
        <ApolloConsumer>
            {(client) => {
                const locationRegex = /([A-Za-zÀ-ÖØ-öø-ÿ])/;

                const getGeoLocationData = async (value: string) => {
                    const { data } = await client.query({
                        query: GEO_LOCATIONS_QUERY,
                        variables: { locationPrefix: value }
                    });

                    return transformGeoLocationData(data.geoSuggestion);
                };

                const getSuggestionFunctionDebounced = AwesomeDebouncePromise(
                    getGeoLocationData,
                    300,
                    {
                        onlyResolvesLast: true
                    }
                );

                return (
                    <InputAutoSuggestWithLabel
                        getData={getSuggestionFunctionDebounced}
                        suggestionSelectedAction={({ id, value }) => {
                            onLocationSelect &&
                                onLocationSelect({
                                    identifier: id,
                                    q: value as string
                                });
                        }}
                        placeholder={forSaleToRentPlaceholder}
                        validInputRegex={locationRegex}
                        showError={showError}
                        id={id}
                        value={value}
                        autoComplete="off"
                        inputOnKeyDown={handleInputKeyDown}
                        inputTextChange={handleInputTextChange}
                        hasExtendedHeight={hasExtendedHeight}
                        hasDarkPurpleBorder={hasDarkPurpleBorder}
                        hasBorderRight={hasBorderRight}
                        onlyDisplayRemoveIconWhenActive={
                            onlyDisplayRemoveIconWhenActive
                        }
                    />
                );
            }}
        </ApolloConsumer>
    );
};
